import * as React from "react";
import { Row, Col } from "@lunchboxinc/lunchbox-components";
import { usePatronContext } from "components/providers/Patron/PatronContext";
import { Logo } from "components/elements";
import { Text, View, Button } from "components/elementsThemed";
import { Tabs, Image, Drawer, Footer } from "components/fragments";
import { Patron } from "components/providers";
import { withTemplate } from "components/hocs";
import { config, images, Routes } from "utils";
import { Login, Logout } from "pages";
import { NavDropdown } from "components/templates/NavDropdown";
import LocationsMap from "../locationsMap";
import LocationSection from "./LocationSection";
import css from "./locationType2.module.scss";

const restaurantIcon = images?.art_logo_2;

const Locations = ({
  clearLocations,
  deliveryChange,
  deliveryFetchCount,
  diningOption,
  displayAddressForm,
  fetching,
  getGeo,
  handleStreetChange,
  locationCardRefs,
  onLocationSelected,
  getPickupGeo,
  googleMapKey,
  locations,
  locationState,
  locationTitle,
  onClickMarker,
  onTabChange,
  style,
  tabOrder,
  isMarketPlace,
  ...props
}) => {
  const { logout, isLoggedIn } = usePatronContext();
  const { address, lat, long } = locationState;
  const { labels, cells, segmentViews, buttons, views } = style;
  const [isLoginOpen, setIsLoginOpen] = React.useState(false);
  const [isLogoutOpen, setIsLogoutOpen] = React.useState(false);
  const toggleLogout = () => {
    setIsLogoutOpen(!isLogoutOpen);
    logout();
  };

  const toggleLogin = () => {
    setIsLoginOpen(!isLoginOpen);
  };

  const ProfileBtn = ({ isLoggedIn }) => {
    if (isLoggedIn) {
      return (
        <NavDropdown
          buttonType={buttons.dropdown}
          onClickProfile={() => props.history.push("/profile")}
          onClickLogout={toggleLogout}
        >
          <Button
            type={buttons.login}
            className={css.login}
            datatest="account-icon"
            role="presentation"
            tabIndex="-1"
            data-cy="account-icon"
          >
            <div>
              <Image
                src={images?.login}
                mediaName="icon_login_1"
                mediaType="svg"
              />
            </div>
            <div>ACCOUNT</div>
          </Button>
        </NavDropdown>
      );
    }
    return (
      <Button
        type={buttons.login}
        className={css.login}
        onClick={toggleLogin}
        data-cy="login-icon"
      >
        <div>
          <Image
            src={images?.icon_login_1}
            mediaName="icon_login_1"
            mediaType="svg"
            title="login"
            alt="Login"
          />
        </div>
        <div>LOGIN</div>
      </Button>
    );
  };

  return (
    <div className={css.container}>
      <Row className={css["location-mobileHeader"]}>
        <View
          className={css["location-mobileHeader--left"]}
          type={views.secondary}
          Component={Col}
          xs="1-2"
        >
          <div className={css.logo}>
            <a href={config.website.url}>
              <Logo src={restaurantIcon} />
            </a>
          </div>
        </View>
        <View
          type={views.background}
          className={css["login-container"]}
          Component={Col}
          xs="1-2"
        >
          {<ProfileBtn isLoggedIn={isLoggedIn} />}
        </View>
      </Row>

      <Row flex className={css["location-header"]}>
        <Col xs="1" sm={{ offset: "1-12", span: "5-6" }}>
          <Row>
            <Col className={css["location-header--left"]} xs="1-2">
              <div className={css.logo}>
                <a href={config.website.url}>
                  <Logo src={restaurantIcon} />
                </a>
              </div>
            </Col>
            <Col xs={{ span: "1-2" }} className={css["login-container"]}>
              {<ProfileBtn isLoggedIn={isLoggedIn} />}
            </Col>
          </Row>
        </Col>
      </Row>

      <Row flex className={css.content}>
        <View
          className={css["map-view"]}
          type={views.secondary}
          Component={Col}
          xs={{ hidden: true }}
          sm={{ hidden: true }}
          md="2-5"
        >
          <div className={css["map-container"]}>
            <LocationsMap
              type={cells.secondary}
              locations={locations}
              googleMapKey={googleMapKey}
              center={{
                lat,
                lng: long,
              }}
              onClickMarker={onClickMarker}
              googleMapURL={Routes.GOOGLE_MAP(googleMapKey)}
              loadingElement={<div style={{ height: "100%" }} />}
              containerElement={<div style={{ height: "100%" }} />}
              mapElement={<div style={{ height: "100%" }} />}
              diningOption={diningOption}
              history={props.history}
              isMarketPlace={isMarketPlace}
            />
          </div>
          <Footer page="location" type={cells.footer} />
        </View>
        <View type={views.background} Component={Col} xs="1" md="3-5">
          <Row className={css["location-list-container"]}>
            <Col
              xs={{ span: "1" }}
              sm={{ offset: "1-12", span: "5-6" }}
              md={{ offset: "1-6", span: "22-24" }}
            >
              <div className={css["search-container"]}>
                <div className={css["title-container"]}>
                  <Text
                    role="heading"
                    aria-level="1"
                    aria-label={locationTitle}
                    type={labels.primary}
                  >
                    {locationTitle}
                  </Text>
                </div>
                <Tabs
                  destroyInactiveTabPane
                  type={segmentViews.standard}
                  activeKey={diningOption}
                  onTabChange={onTabChange}
                >
                  {tabOrder.map((option) => (
                    <div key={option.diningOption} title={option.name}>
                      <Col xs="1" className={css["form-container"]}>
                        {displayAddressForm &&
                          option.form({
                            address,
                            clearLocations,
                            deliveryChange,
                            getGeo,
                            getPickupGeo,
                            handleStreetChange,
                          })}
                      </Col>
                    </div>
                  ))}
                </Tabs>
                <Row>
                  <Col xs={{ offset: "1-24", span: "11-12" }}>
                    <LocationSection
                      fetching={fetching}
                      diningOption={diningOption}
                      locations={locations}
                      style={style}
                      deliveryFetchCount={deliveryFetchCount}
                      onLocationSelected={onLocationSelected}
                      locationCardRefs={locationCardRefs}
                    />
                  </Col>
                </Row>
              </div>

              <Footer
                page="location"
                type={cells.footer}
                className={css.footer}
              />
            </Col>
          </Row>
        </View>
      </Row>

      <Drawer.ResponsiveDrawer
        isOpen={isLoginOpen}
        onChange={toggleLogin}
        ariaLabel="Login"
      >
        <Login onComplete={toggleLogin} />
      </Drawer.ResponsiveDrawer>

      <Drawer.ResponsiveDrawer
        isOpen={isLogoutOpen}
        onChange={toggleLogout}
        ariaLabel="Logout"
      >
        <Logout
          onConfirm={() => {
            // hide logout panel
            toggleLogout();
            // perform logout api call
            logout();
          }}
          onCancel={toggleLogout}
        />
      </Drawer.ResponsiveDrawer>
    </div>
  );
};

export default withTemplate(Locations, "location");
